import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import EmailIcon from '@mui/icons-material/Email'
import InfoIcon from '@mui/icons-material/Info'
import ReplayIcon from '@mui/icons-material/Replay'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import RedoIcon from '@mui/icons-material/Redo'
import SaveIcon from '@mui/icons-material/Save'
import UndoIcon from '@mui/icons-material/Undo'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt'
import ViewStreamIcon from '@mui/icons-material/ViewStream'
import LinkIcon from '@mui/icons-material/Link'
import VisibilityIcon from '@mui/icons-material/Visibility'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import { RetryMode } from '@src/api/document-api'
import { useRegenSAS } from '@src/api/document-pages-api'
import MatchDialog from '@src/containers/DocsAutomation/components/MatchDialog'
import MessageDialog from '@src/containers/DocsAutomation/components/MessageDialog'
import { RetryDialog } from './RetryDialog'
import { DocsAutomation, EProcessingStatus, getFinalizationBlockMessage } from '@src/types/automationDocument'
import { DocumentPage } from '@src/types/documentPage'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface PagesToolbarProps {
  document: DocsAutomation
  documentTypes: string[]
  viewOnly?: boolean
  isEditMode: boolean
  isFinalizing: boolean
  selectedPages: Set<string>
  sentPageIds: Set<string>
  selectedDocType: string
  showDiscarded: boolean
  columns: number
  allPages: DocumentPage[]
  visiblePages: DocumentPage[]
  isDeletePending: boolean
  canUndo: boolean
  canRedo: boolean
  canUndoAll: boolean
  canSave: boolean
  isSaving: boolean
  isDirty: boolean
  onUndo: () => void
  onRedo: () => void
  onUndoAll: () => void
  onSave: () => void
  onToggleEditMode: () => void
  onFinalize: () => void
  onDeleteSelected: () => void
  onOpenChangeTypeDialog: () => void
  onDeselectAll: () => void
  onDocTypeChange: (event: SelectChangeEvent) => void
  onShowDiscardedChange: (checked: boolean) => void
  onColumnsChange: (_event: React.MouseEvent<HTMLElement>, newColumns: number | null) => void
  pageHeader: (page: DocumentPage) => string
  onOpenLinkDialog: () => void
  onRetry: (mode: RetryMode) => void
  isRetrying: boolean
}

export const PagesToolbar = ({
  document,
  documentTypes,
  viewOnly,
  isEditMode,
  isFinalizing,
  selectedPages,
  sentPageIds,
  selectedDocType,
  showDiscarded,
  columns,
  allPages,
  visiblePages,
  isDeletePending,
  canUndo,
  canRedo,
  canUndoAll,
  canSave,
  isSaving,
  isDirty,
  onUndo,
  onRedo,
  onUndoAll,
  onSave,
  onToggleEditMode,
  onFinalize,
  onDeleteSelected,
  onOpenChangeTypeDialog,
  onDeselectAll,
  onDocTypeChange,
  onShowDiscardedChange,
  onColumnsChange,
  pageHeader,
  onOpenLinkDialog,
  onRetry,
  isRetrying,
}: PagesToolbarProps) => {
  const { t, i18n } = useTranslation()

  const hasDiscardedPages = useMemo(() => {
    return allPages.some((page) => page.processingStatus === EProcessingStatus.Discarded)
  }, [allPages])

  const finalizationBlockMessage = useMemo((): string | null => {
    const messageKey = getFinalizationBlockMessage(document.linkedCreditApp)
    if (!messageKey) return null

    if (messageKey === 'docsAutomation.programNotSupported') {
      const programName =
        document.linkedCreditApp?.productLine === 2 ? 'IFX' : `Program ${document.linkedCreditApp?.productLine}`
      return t(messageKey as 'docsAutomation.programNotSupported', { program: programName })
    }

    if (messageKey === 'docsAutomation.cannotSendDocuments') {
      const status = i18n.language === 'en' ? document.linkedCreditApp?.statusEn : document.linkedCreditApp?.status
      return t(messageKey as 'docsAutomation.cannotSendDocuments', { status })
    }

    return t(messageKey as 'docsAutomation.mustLinkForFinalization')
  }, [document.linkedCreditApp, t, i18n.language])

  const isPartiallySent = document.processingStatus === EProcessingStatus.PartiallySent
  const canFinalize = document.linkedCreditApp && !finalizationBlockMessage && !isEditMode && !viewOnly && !isDirty

  const allSelectedAreSent = selectedPages.size > 0 && [...selectedPages].every((id) => sentPageIds.has(id))

  const [showMessageDialog, setShowMessageDialog] = useState(false)
  const [showMatchDialog, setShowMatchDialog] = useState(false)
  const [showRetryDialog, setShowRetryDialog] = useState(false)

  const RETRYABLE_STATUSES = new Set([EProcessingStatus.Extracted, EProcessingStatus.Processed, EProcessingStatus.Matched, EProcessingStatus.Failed])
  const PROCESSING_STATUSES = new Set([  EProcessingStatus.Processed, EProcessingStatus.Pending, EProcessingStatus.PendingBatch, EProcessingStatus.Stored, EProcessingStatus.Processing, EProcessingStatus.Classified, EProcessingStatus.Extracted])

  const isStale = useMemo(() => {
    const updatedDate = new Date(document.updatedOn)
    const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000)
    return updatedDate < tenMinutesAgo
  }, [document.updatedOn])

  const isCurrentlyProcessing = PROCESSING_STATUSES.has(document.processingStatus)
  const canRetry =
    !viewOnly &&
    !isEditMode &&
    (RETRYABLE_STATUSES.has(document.processingStatus) || (isCurrentlyProcessing && isStale))

  const { mutateAsync: regenSas, isPending: isRegenerating } = useRegenSAS(document.id || '')
  const handleRegenSAS = () => {
    if (!document.id || !document.versionTag) return

    void regenSas()
  }

  const editButtonLabel = isEditMode ? (isDirty ? t('common.cancel') : t('common.done')) : t('common.edit')

  return (
    <>
      <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', px: 0, minHeight: '48px !important', gap: 2, mb: 1 }}>
        {/* Left side - Edit Mode Controls */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {!viewOnly && (
            <>
              <Button
                variant={isEditMode ? 'contained' : 'outlined'}
                size="small"
                disabled={isFinalizing || isSaving}
                startIcon={isEditMode ? <VisibilityIcon /> : <EditIcon />}
                onClick={onToggleEditMode}
                data-tour="edit-mode-btn"
              >
                {editButtonLabel}
              </Button>

              {canFinalize && (
                <Button
                  variant="contained"
                  size="small"
                  color={isPartiallySent ? 'warning' : 'success'}
                  onClick={onFinalize}
                  disabled={isFinalizing}
                  loading={isFinalizing}
                  data-tour="finalize-btn"
                >
                  {isPartiallySent ? t('docsAutomation.retryFinalization' as 'common.close') : t('common.finalize')}
                </Button>
              )}

              {canRetry && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ReplayIcon />}
                  onClick={() => setShowRetryDialog(true)}
                  disabled={isRetrying || isFinalizing}
                  loading={isRetrying}
                  data-tour="retry-btn"
                >
                  {t('docsAutomation.retry')}
                </Button>
              )}
            </>
          )}

          {!viewOnly && (isEditMode || isDirty) && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              <Tooltip title={t('common.undo')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={onUndo}
                    disabled={!canUndo || isSaving || isFinalizing}
                    data-tour="undo-btn"
                  >
                    <UndoIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={t('common.redo')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={onRedo}
                    disabled={!canRedo || isSaving || isFinalizing}
                    data-tour="redo-btn"
                  >
                    <RedoIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={t('docsAutomation.undoAllTooltip')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={onUndoAll}
                    disabled={!canUndoAll || isSaving || isFinalizing}
                    data-tour="undo-all-btn"
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              <Tooltip title={t('common.save')}>
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<SaveIcon fontSize="small" />}
                    onClick={onSave}
                    disabled={!canSave || isSaving || isFinalizing}
                    loading={isSaving}
                    data-tour="save-btn"
                  >
                    {t('common.save')}
                  </Button>
                </span>
              </Tooltip>
            </>
          )}

          <Button onClick={handleRegenSAS} disabled={isFinalizing} data-tour="regen-sas-btn">{isRegenerating ? <CircularProgress size={20} /> : 'Regen SAS'}</Button>

          {isEditMode && (
            <>
              <Tooltip title={allSelectedAreSent ? t('docsAutomation.pageAlreadySent' as 'common.close') : t('common.delete')}>
                <span>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteIcon fontSize="small" />}
                    onClick={onDeleteSelected}
                    disabled={selectedPages.size === 0 || isDeletePending || isFinalizing || isSaving || allSelectedAreSent}
                    loading={isDeletePending}
                  >
                    {t('common.delete')}
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title={allSelectedAreSent ? t('docsAutomation.pageAlreadySent' as 'common.close') : t('common.changeDocumentType')}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onOpenChangeTypeDialog}
                    disabled={selectedPages.size === 0 || isDeletePending || isFinalizing || isSaving || allSelectedAreSent}
                  >
                    {t('common.changeDocumentType')}
                  </Button>
                </span>
              </Tooltip>

              <Button
                size="small"
                variant="text"
                onClick={onDeselectAll}
                disabled={selectedPages.size === 0 || isDeletePending || isFinalizing}
              >
                {t('common.deselectAll')}
              </Button>

              {selectedPages.size > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {t('common.selectedCount', { count: selectedPages.size })}
                </Typography>
              )}
              {selectedPages.size === 0 && selectedDocType !== 'all' && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {t('common.dragToReorder')}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Right side - View Controls */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 'auto' }}>
          {hasDiscardedPages && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={showDiscarded}
                onChange={(e) => onShowDiscardedChange(e.target.checked)}
                size="small"
              />
              <Typography variant="caption">{t('common.showDiscarded' as 'common.close')}</Typography>
            </Box>
          )}

          {document.email?.message && (
            <Tooltip title={t('docsAutomation.message')}>
              <Button  variant="outlined" startIcon={<EmailIcon />} onClick={() => setShowMessageDialog(true)} data-tour="message-btn">
                {t('docsAutomation.message')}
              </Button>
            </Tooltip>
          )}

          <Tooltip title={t('docsAutomation.notes')}>
            <Button variant="outlined" startIcon={<InfoIcon />} onClick={() => setShowMatchDialog(true)} data-tour="document-notes-btn">
              {t('docsAutomation.notes')}
            </Button>
          </Tooltip>

          <FormControl size="small" sx={{ width: 'auto' }} data-tour="doctype-filter">
            <InputLabel>{t('common.documentType')}</InputLabel>
            <Select value={selectedDocType} label={t('common.documentType')} onChange={onDocTypeChange}>
              <MenuItem value="all">{t('common.allTypes', { count: visiblePages.length })}</MenuItem>
              {documentTypes.map((type) => {
                const count = visiblePages.filter((page) => {
                  const typeName = pageHeader(page).split(' - ')[0].trim()
                  return typeName === type
                }).length
                return (
                  <MenuItem key={type} value={type}>
                    {type} ({count})
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>

          <ToggleButtonGroup value={columns} exclusive onChange={onColumnsChange} size="small" data-tour="page-controls">
            <ToggleButton value={1} aria-label="1 column">
              <ViewStreamIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value={2} aria-label="2 columns">
              <ViewColumnIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value={3} aria-label="3 columns">
              <ViewQuiltIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value={4} aria-label="4 columns">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Toolbar>

      {finalizationBlockMessage && !viewOnly && (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={
            !document.linkedCreditApp ? (
              <Button color="warning" size="small" startIcon={<LinkIcon />} onClick={onOpenLinkDialog}>
                {t('docsAutomation.manualLink' as 'common.close')}
              </Button>
            ) : undefined
          }
        >
          {finalizationBlockMessage}
        </Alert>
      )}

      {document.warnings?.length > 0 && (
        <Stack gap={0.5} mb={2}>
          {document.warnings.map((warning, index) => (
            <Alert
              key={index}
              severity="warning"
              variant="outlined"
              icon={<WarningAmberIcon fontSize="small" />}
              sx={{ py: 0 }}
            >
              {t(`enum.warningCode.${warning.code}` as never, { fileName: warning.fileName })}
            </Alert>
          ))}
        </Stack>
      )}

      <MessageDialog
        docsAutomation={showMessageDialog ? document : null}
        onClose={() => setShowMessageDialog(false)}
      />

      <MatchDialog
        docsAutomation={showMatchDialog ? document : null}
        onClose={() => setShowMatchDialog(false)}
      />

      <RetryDialog
        open={showRetryDialog}
        allPages={allPages}
        onClose={() => setShowRetryDialog(false)}
        onConfirm={(mode) => {
          onRetry(mode)
          setShowRetryDialog(false)
        }}
        isLoading={isRetrying}
        isCurrentlyProcessing={isCurrentlyProcessing}
        isFinalizing={isFinalizing}
      />
    </>
  )
}
